import { Injectable } from "@angular/core"
import { BehaviorSubject } from "rxjs"
import { StreamingService } from "./streaming.service"
import { ParentClass } from "../../parent"
import {
    CurrentSongAudioBuffer,
    PlayerState,
    PlayerStore,
} from "@services/audio/player.store"
import {
    CurrentSong,
    ISongQueueEntry,
    SongQueue,
} from "@common-lib/classes/songQueue"
import { cloneDeep } from "lodash"
import {
    PlaybackActionType,
    PlaybackEngine,
} from "@services/audio/playbackEngine"
import {
    StreamingBufferV2Metadata,
    UpdateTrackLoadingStatus,
} from "@common-lib/interfaces/api/sockets"
import { Query } from "@datorama/akita"
import { MAX_LOADING } from "@common-lib/constants/constants"

export class PlayerQuery extends Query<PlayerState> {
    constructor(protected store: PlayerStore) {
        super(store)
    }

    public isPlaying$ = this.select(state => state.status === "playing")
    public currentSong$ = this.select(state => state.songQueue.currentSong)
    public bufferLoadingProgress$ = this.select(
        state => state.bufferLoadingProgress
    )
    public playbackEngine$ = this.select(state => state.playbackEngine)
    public timeElapsed$ = this.select(state => state.timeElapsed)
    public audioBufferForRMS$ = this.select(state => state.audioBufferForRMS)

    public get isPlaying(): boolean {
        return this.getValue().status === "playing"
    }

    public get timeElapsed(): number {
        return this.getValue().timeElapsed
    }

    public get currentSong(): CurrentSong | null {
        return this.getValue().songQueue.currentSong
    }

    public get bufferLoadingProgress(): number {
        return this.getValue().bufferLoadingProgress
    }

    public get audioBufferForRMS(): {
        endTime: number
        data: AudioBuffer | undefined
    } {
        return this.getValue().audioBufferForRMS
    }
}

@Injectable()
export class PlayerService extends ParentClass {
    public static SAMPLE_RATE = 32000

    public navigate$: BehaviorSubject<string | undefined> = new BehaviorSubject<
        string | undefined
    >(undefined)

    public readonly store: PlayerStore = new PlayerStore()

    public query = new PlayerQuery(this.store)

    private static MAX_CACHE_SIZE = 50

    /** Invalidates asynchronous work started for an older queue selection. */
    private loadGeneration = 0
    private endingTrackID: string | null = null
    private autoplayGeneration: number | null = null

    private audioContext: AudioContext | null = new window.AudioContext({
        sampleRate: PlayerService.SAMPLE_RATE,
    })

    constructor(private streamingService: StreamingService) {
        super()
    }

    private get state(): PlayerState {
        return this.store.getValue()
    }

    public setVolume(volume: number) {
        this.store.updateStore({
            volume,
        })

        this.state.playbackEngine?.setVolume(volume)
    }

    public play() {
        if (
            this.state.songQueue.currentSong === null ||
            this.state.status === "playing"
        ) {
            return
        }

        if (this.state.playbackEngine === undefined) {
            return
        }

        this.state.playbackEngine.registerAction(
            PlaybackActionType.PLAY,
            this.query.timeElapsed
        )

        this.store.updateStore({
            status: "playing",
        })
    }

    public pause() {
        if (
            this.state.songQueue.currentSong === null ||
            this.state.status === "paused"
        ) {
            return
        }

        if (this.state.playbackEngine === undefined) {
            this.store.updateStore({ status: "paused" })
            return
        }

        this.state.playbackEngine.registerAction(
            PlaybackActionType.PAUSE,
            this.query.timeElapsed
        )

        this.store.updateStore({
            status: "paused",
        })
    }

    public seek(timePercentage: number) {
        if (this.state.songQueue.currentSong === null) {
            return
        }

        if (this.state.playbackEngine === undefined) {
            return
        }

        const duration = this.state.songQueue.currentSong.song.duration
        const percentage = Number.isFinite(timePercentage)
            ? Math.max(0, Math.min(100, timePercentage))
            : 0

        this.state.playbackEngine.registerAction(
            PlaybackActionType.SEEK,
            Math.min(duration, (duration * percentage) / 100)
        )
    }

    public async setSongQueue(songQueue: SongQueue, shouldPlayNext: boolean) {
        const currentSongID = this.query.currentSong
            ? this.query.currentSong.song.id
            : null

        // if the currently playing songs are the same, then we dont want to pause / play anything
        // and instead, just want to update the song queue in the store
        if (
            songQueue.currentSong !== null &&
            songQueue.currentSong.song.id === currentSongID
        ) {
            return this.store.updateStore({
                songQueue,
            })
        }

        this.pause()

        const generation = this.beginTrackTransition(shouldPlayNext)
        await this.loadSongQueue(songQueue, generation)

        if (shouldPlayNext && this.isCurrentGeneration(generation)) {
            this.play()
        }
    }

    public async updateGeneratedSongCache(
        trackID: string,
        loading: number,
        buffer: ArrayBuffer | ArrayBufferView,
        bufferStart: number,
        bufferEnd: number,
        index: number
    ) {
        // @todo: remove from generated song cache if loading === MAX_LOADING

        const normalizedBuffer = this.normalizeBuffer(buffer)

        const audioBuffer: CurrentSongAudioBuffer = {
            start: bufferStart,
            end: bufferEnd,
            buffer: normalizedBuffer,
        }

        const buffers = [audioBuffer]
        // this.state.generatedSongCache[trackID] != null &&
        // this.state.generatedSongCache[trackID].length > 0
        //     ? this.state.generatedSongCache[trackID].concat([audioBuffer])
        //     : [audioBuffer]

        this.addSongToGeneratedCache(trackID, buffers)

        const generation = this.loadGeneration
        const engine = this.state.playbackEngine
        if (this.isCurrentTrack(trackID, generation, engine)) {
            const playableBuffer = await this.prepareGeneratedBufferForPlayback(
                audioBuffer
            )
            if (
                playableBuffer === null ||
                !this.isCurrentTrack(trackID, generation, engine)
            ) {
                this.updateCurrentSongPlayback(trackID, { loading })
                return
            }

            await engine.hotLoadBuffer(playableBuffer, index)
            if (!this.isCurrentTrack(trackID, generation, engine)) {
                return
            }
            this.updateCurrentSongPlayback(trackID, {
                loading,
                duration: Math.max(
                    this.query.currentSong?.song.duration ?? 0,
                    playableBuffer.end,
                    bufferEnd
                ),
            })
        }
    }

    public updateCurrentSongPlayback(
        trackID: string,
        patch: { duration?: number; loading?: number }
    ): void {
        const current = this.state.songQueue.currentSong
        if (current === null || current.song.id !== trackID) {
            return
        }

        const song = { ...current.song }

        if (
            typeof patch.duration === "number" &&
            Number.isFinite(patch.duration) &&
            patch.duration > song.duration
        ) {
            song.duration = patch.duration
        }

        if (
            typeof patch.loading === "number" &&
            Number.isFinite(patch.loading)
        ) {
            song.loading = Math.max(song.loading, Math.min(100, patch.loading))
        }

        const songQueue = cloneDeep(this.state.songQueue)
        songQueue.currentSong = {
            ...current,
            song,
        }
        this.store.updateStore({
            songQueue,
        })
    }

    public async next() {
        if (this.state.songQueue.currentSong === null) {
            return
        }

        const shouldPlayNext = this.state.status === "playing"

        this.pause()

        if (!this.state.songQueue.hasNextSong()) {
            ++this.loadGeneration
            this.endingTrackID = this.query.currentSong.song.id
            this.autoplayGeneration = null
            this.streamingService.cancelCurrentStream()
            return
        }

        const queue = cloneDeep(this.state.songQueue)
        queue.playNext()
        const generation = this.beginTrackTransition(shouldPlayNext)

        await this.loadSongQueue(queue, generation)

        if (shouldPlayNext && this.isCurrentGeneration(generation)) {
            this.play()
        }
    }

    public async previous() {
        if (this.state.songQueue.currentSong === null) {
            return
        }

        if (
            !this.state.songQueue.hasPreviousSong() ||
            this.query.timeElapsed > 2
        ) {
            this.seek(0)
        } else {
            const shouldPlayNext = this.state.status === "playing"

            if (shouldPlayNext) {
                this.pause()
            }

            const queue: SongQueue = cloneDeep(this.state.songQueue)
            queue.playPrevious()
            const generation = this.beginTrackTransition(shouldPlayNext)

            await this.loadSongQueue(queue, generation)

            if (shouldPlayNext && this.isCurrentGeneration(generation)) {
                this.play()
            }
        }
    }

    private beginTrackTransition(shouldAutoplay = false): number {
        const generation = ++this.loadGeneration
        this.endingTrackID = null
        this.autoplayGeneration = shouldAutoplay ? generation : null
        this.streamingService.cancelCurrentStream()
        this.resetBufferLoadingProgress()
        return generation
    }

    private isCurrentGeneration(generation: number): boolean {
        return generation === this.loadGeneration
    }

    private isCurrentTrack(
        trackID: string,
        generation: number,
        engine?: PlaybackEngine
    ): boolean {
        return (
            this.isCurrentGeneration(generation) &&
            this.query.currentSong?.song.id === trackID &&
            (engine === undefined || this.state.playbackEngine === engine)
        )
    }

    private async loadSongQueue(songQueue: SongQueue, generation: number) {
        if (!this.isCurrentGeneration(generation)) {
            return
        }

        const currentSongID = this.state.songQueue.currentSong
            ? this.state.songQueue.currentSong.song.id
            : null

        const nextSongID = songQueue.currentSong?.song.id ?? null

        this.store.updateStore({
            songQueue,
            ...(nextSongID !== currentSongID ? { timeElapsed: 0 } : {}),
        })

        if (!this.isCurrentGeneration(generation)) {
            return
        }

        if (songQueue.currentSong === null) {
            await this.destroyPlaybackEngine(generation)
            return
        }

        if (songQueue.currentSong.song.id === currentSongID) {
            return
        }

        return this.loadCurrentSong(songQueue.currentSong, generation)
    }

    private async loadCurrentSong(
        currentSong: CurrentSong,
        generation: number
    ) {
        const engine = await this.initPlaybackEngine(
            generation,
            currentSong.song.id
        )
        if (
            engine === undefined ||
            !this.isCurrentTrack(currentSong.song.id, generation, engine)
        ) {
            return
        }

        const useWebAudioAPI = !engine.mediaSourceSupport

        if (useWebAudioAPI) {
            await this.loadWithWebAudioAPI(currentSong.song, generation, engine)
        } else {
            await this.loadWithMediaSourceAPI(
                currentSong.song,
                generation,
                engine
            )
        }
    }

    private async loadWithWebAudioAPI(
        song: ISongQueueEntry,
        generation: number,
        engine: PlaybackEngine
    ) {
        let buffers: CurrentSongAudioBuffer[] = []

        if (this.songIsCached(song.id)) {
            buffers = this.getCachedSongAudio(song.id)
        } else {
            buffers =
                (await this.streamingService.streamContent(
                    song.id,
                    async () => {}
                )) ?? []
            if (!this.isCurrentTrack(song.id, generation, engine)) {
                return
            }
        }

        if (
            buffers.length === 0 ||
            !this.isCurrentTrack(song.id, generation, engine)
        ) {
            return
        }

        if (!this.songIsCached(song.id)) {
            this.addSongToCache(song.id, buffers)
        }

        try {
            const decodedBuffer =
                await this.decodeAllBuffersToSingleAudioBuffer(buffers)
            if (!this.isCurrentTrack(song.id, generation, engine)) {
                return
            }

            await engine.addBuffer(
                {
                    start: 0,
                    end: decodedBuffer.duration,
                    buffer: decodedBuffer,
                },
                0
            )

            if (!this.isCurrentTrack(song.id, generation, engine)) {
                return
            }

            this.updateCurrentSongPlayback(song.id, {
                duration: decodedBuffer.duration,
                loading: song.loading,
            })

            this.store.updateStore({
                bufferLoadingProgress: 100,
            })
        } catch {
            // Incomplete generating audio may not decode until more chunks arrive.
        }
    }

    private resetBufferLoadingProgress() {
        this.store.updateStore({
            bufferLoadingProgress: 0,
            audioBufferForRMS: {
                endTime: 0,
                data: undefined,
            },
        })
    }

    private async loadWithMediaSourceAPI(
        song: ISongQueueEntry,
        generation: number,
        engine: PlaybackEngine
    ) {
        if (this.songIsCached(song.id)) {
            const buffers = this.getCachedSongAudio(song.id)

            let index = 0

            for (const buffer of buffers) {
                if (!this.isCurrentTrack(song.id, generation, engine)) {
                    return
                }
                await engine.addBuffer(buffer, index)
                index++
            }

            const playableEnd = Math.max(
                ...buffers.map(buffer => buffer.end),
                song.duration
            )
            this.updateCurrentSongPlayback(song.id, {
                duration: playableEnd,
                loading: song.loading,
            })
        } else {
            if (song.loading !== MAX_LOADING) {
                return
            }

            this.resetBufferLoadingProgress()

            const buffers: CurrentSongAudioBuffer[] = []
            const receivedOrders = new Set<number>()
            let highestOrder = -1

            await this.streamingService.streamContent(
                song.id,
                (async (data: StreamingBufferV2Metadata) => {
                    if (
                        !this.isCurrentTrack(song.id, generation, engine) ||
                        receivedOrders.has(data.order)
                    ) {
                        return
                    }

                    receivedOrders.add(data.order)
                    highestOrder = Math.max(highestOrder, data.order)
                    const audioBuffer: CurrentSongAudioBuffer = {
                        start: data.from,
                        end: data.to,
                        buffer: data.audio,
                    }

                    buffers.push(audioBuffer)

                    await engine.addBuffer(audioBuffer, data.order)

                    if (this.isCurrentTrack(song.id, generation, engine)) {
                        const progress =
                            song.duration > 0 && Number.isFinite(data.to)
                                ? Math.min(99, (data.to / song.duration) * 100)
                                : 0
                        this.store.updateStore({
                            bufferLoadingProgress: Math.max(
                                this.state.bufferLoadingProgress,
                                progress
                            ),
                        })
                    }
                }).bind(this)
            )

            if (!this.isCurrentTrack(song.id, generation, engine)) {
                return
            }

            await engine.addUnsortedBuffers()

            const hasContiguousOrders =
                highestOrder >= 0 &&
                Array.from({ length: highestOrder + 1 }).every((_, index) =>
                    receivedOrders.has(index)
                )
            const streamedEnd = Math.max(
                0,
                ...buffers.map(buffer => buffer.end)
            )
            const reachesExpectedDuration =
                !(song.duration > 0) || streamedEnd + 0.05 >= song.duration
            if (!hasContiguousOrders || !reachesExpectedDuration) {
                return
            }

            this.addSongToCache(song.id, buffers)
        }

        if (this.isCurrentTrack(song.id, generation, engine)) {
            this.store.updateStore({
                bufferLoadingProgress: 100,
            })
        }
    }

    private addSongToGeneratedCache(
        songID: string,
        buffers: CurrentSongAudioBuffer[]
    ) {
        this.store.updateStore({
            generatedSongCache: {
                ...this.state.generatedSongCache,
                [songID]: buffers,
            },
        })
    }

    public removeGeneratedSongFromCache(songID: string) {
        this.removeSongFromGeneratedCache(songID)
    }

    private addSongToCache(songID: string, buffers: CurrentSongAudioBuffer[]) {
        let songAudioCache = { ...this.state.songAudioCache }

        if (songAudioCache[songID] !== undefined) {
            return
        }

        if (
            Object.keys(this.state.songAudioCache).length + 1 >
            PlayerService.MAX_CACHE_SIZE
        ) {
            const cachedSongs = Object.keys(this.state.songAudioCache)
                .map(key => {
                    return { ...this.state.songAudioCache[key], key }
                })
                .sort((a, b) => a.lastAccessed - b.lastAccessed)

            const songToRemove = cachedSongs[0]
            delete songAudioCache[songToRemove.key]
        }

        songAudioCache[songID] = {
            lastAccessed: Date.now(),
            buffers,
        }

        this.store.updateStore({
            songAudioCache,
        })
    }

    private removeSongFromGeneratedCache(songID: string) {
        const songAudioCache = { ...this.state.generatedSongCache }

        if (songAudioCache[songID] === undefined) {
            return
        }

        delete songAudioCache[songID]

        this.store.updateStore({
            generatedSongCache: songAudioCache,
        })
    }

    private removeSongFromCache(songID: string) {
        const songAudioCache = { ...this.state.songAudioCache }

        if (songAudioCache[songID] === undefined) {
            return
        }

        delete songAudioCache[songID]

        this.store.updateStore({
            songAudioCache,
        })
    }

    public songIsCached(id: string): boolean {
        return (
            this.state.songAudioCache.hasOwnProperty(id) ||
            this.state.generatedSongCache.hasOwnProperty(id)
        )
    }

    private getCachedSongAudio(id: string): CurrentSongAudioBuffer[] {
        if (this.state.generatedSongCache.hasOwnProperty(id)) {
            return this.state.generatedSongCache[id]
        }

        const cached = this.state.songAudioCache[id]
        this.store.updateStore({
            songAudioCache: {
                ...this.state.songAudioCache,
                [id]: { ...cached, lastAccessed: Date.now() },
            },
        })

        return cached.buffers
    }

    private normalizeBuffer(
        buffer: ArrayBuffer | ArrayBufferView
    ): ArrayBuffer {
        if (buffer instanceof ArrayBuffer) {
            return buffer
        }

        return buffer.buffer.slice(
            buffer.byteOffset,
            buffer.byteOffset + buffer.byteLength
        )
    }

    private async initPlaybackEngine(
        generation: number,
        trackID: string
    ): Promise<PlaybackEngine | undefined> {
        const previousEngine = this.state.playbackEngine
        if (previousEngine) {
            await previousEngine.destroy()
        }

        if (!this.isCurrentTrack(trackID, generation)) {
            return undefined
        }

        let engine: PlaybackEngine
        engine = new PlaybackEngine(
            (() => {
                return this.state
            }).bind(this),
            ((time: number) => {
                if (!this.isCurrentTrack(trackID, generation, engine)) {
                    return
                }
                this.store.updateStore({
                    timeElapsed: time,
                })
            }).bind(this),
            (async () => {
                if (
                    !this.isCurrentTrack(trackID, generation, engine) ||
                    this.endingTrackID === trackID
                ) {
                    return
                }

                this.endingTrackID = trackID
                await this.next()
            }).bind(this),
            (async (buffers: CurrentSongAudioBuffer[]) => {
                if (!this.isCurrentTrack(trackID, generation, engine)) {
                    return
                }
                try {
                    const firstBuffer = buffers[0]?.buffer
                    const audioBuffer =
                        firstBuffer instanceof AudioBuffer
                            ? firstBuffer
                            : await this.decodeAllBuffersToSingleAudioBuffer(
                                  buffers
                              )

                    if (!this.isCurrentTrack(trackID, generation, engine)) {
                        return
                    }

                    this.store.updateStore({
                        audioBufferForRMS: {
                            endTime: buffers[buffers.length - 1].end,
                            data: audioBuffer,
                        },
                    })
                } catch {
                    // Generating MP3 chunks may not form a decodable file yet.
                }
            }).bind(this),
            (() => {
                if (this.isCurrentTrack(trackID, generation, engine)) {
                    this.store.updateStore({ status: "paused" })
                }
            }).bind(this)
        )

        if (!this.isCurrentTrack(trackID, generation)) {
            await engine.destroy()
            return undefined
        }

        this.store.updateStore({ playbackEngine: engine })
        if (this.autoplayGeneration === generation) {
            this.play()
        }
        return engine
    }

    private async destroyPlaybackEngine(generation: number): Promise<void> {
        const engine = this.state.playbackEngine
        if (this.isCurrentGeneration(generation)) {
            this.store.updateStore({ playbackEngine: undefined })
        }
        await engine?.destroy()
    }

    /** Releases player and transport resources, for logout and host teardown. */
    public async teardown(): Promise<void> {
        const generation = ++this.loadGeneration
        this.endingTrackID = null
        this.autoplayGeneration = null
        this.streamingService.cancelCurrentStream()
        const engine = this.state.playbackEngine
        const audioContext = this.audioContext
        this.audioContext = null

        this.store.updateStore({
            status: "paused",
            songQueue: new SongQueue(),
            playbackEngine: undefined,
            timeElapsed: 0,
            bufferLoadingProgress: 0,
            audioBufferForRMS: { endTime: 0, data: undefined },
        })

        await engine?.destroy()
        if (audioContext?.state !== "closed") {
            await audioContext?.close().catch(() => undefined)
        }
        if (!this.isCurrentGeneration(generation)) {
            return
        }
    }

    /**
     * Merges an array of ArrayBuffers into a single ArrayBuffer.
     *
     * @param buffers - An array of ArrayBuffers to be merged.
     * @returns A Promise that resolves to the merged ArrayBuffer.
     */
    private async mergeBuffers(buffers: ArrayBuffer[]): Promise<ArrayBuffer> {
        // Calculate the total length of all ArrayBuffers
        const totalLength = buffers.reduce(
            (sum, buffer) => sum + buffer.byteLength,
            0
        )

        // Create a new ArrayBuffer with the total length
        const mergedBuffer = new Uint8Array(totalLength)

        // Copy each ArrayBuffer into the new ArrayBuffer
        let offset = 0
        for (const buffer of buffers) {
            mergedBuffer.set(new Uint8Array(buffer), offset)
            offset += buffer.byteLength
        }

        return mergedBuffer.buffer
    }

    syncStream(buffer: ArrayBuffer): ArrayBuffer {
        const buf8 = new Uint8Array(buffer)

        for (let i = 0; i < buf8.length - 1; i++) {
            if (buf8[i] === 0xff && (buf8[i + 1] & 0xe0) === 0xe0) {
                return buffer.slice(i)
            }
        }

        return buffer
    }

    private async prepareGeneratedBufferForPlayback(
        audioBuffer: CurrentSongAudioBuffer
    ): Promise<CurrentSongAudioBuffer | null> {
        if (
            this.state.playbackEngine?.mediaSourceSupport ||
            !(audioBuffer.buffer instanceof ArrayBuffer)
        ) {
            return audioBuffer
        }

        try {
            const decoded = await this.decodeBufferToAudioBuffer(
                audioBuffer.buffer.slice(0),
                "prepareGeneratedBufferForPlayback"
            )

            // A generated snapshot is cumulative. A short standalone MP3
            // continuation must not replace the longer active fallback source.
            if (decoded.duration + 0.05 < audioBuffer.end) {
                return null
            }

            return {
                start: 0,
                end: decoded.duration,
                buffer: decoded,
            }
        } catch {
            return null
        }
    }

    private async decodeBufferToAudioBuffer(
        arrayBuffer: ArrayBuffer,
        source: string
    ): Promise<AudioBuffer> {
        try {
            if (
                this.audioContext === null ||
                this.audioContext.state === "closed"
            ) {
                this.audioContext = new window.AudioContext({
                    sampleRate: PlayerService.SAMPLE_RATE,
                })
            }
            return await this.audioContext.decodeAudioData(arrayBuffer.slice(0))
        } catch (error) {
            throw new Error(
                `Error when decoding buffer in ${source}: ${String(error)}`
            )
        }
    }

    private async decodeAllBuffersToSingleAudioBuffer(
        buffers: CurrentSongAudioBuffer[]
    ): Promise<AudioBuffer> {
        // 1: Sort the buffers based on the start time and prepare the merge
        buffers.sort((a, b) => a.start - b.start)

        const arrayBuffers = []

        for (let i = 0; i < buffers.length; i++) {
            const sourceBuffer = buffers[i].buffer as ArrayBuffer
            arrayBuffers.push(this.syncStream(sourceBuffer))
        }

        // Step 2: merge the array buffers
        const mergedBuffer = await this.mergeBuffers(arrayBuffers)

        // Step 3: decode the merged buffer to an audio buffer
        const audioBuffer = await this.decodeBufferToAudioBuffer(
            mergedBuffer,
            "decodeAllBuffersToSingleAudioBuffer"
        )

        return audioBuffer
    }
}
